/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { ApiRequest } from '../../../interface/common.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ocpiApi from '../../../utils/api/ocpi';

import {
  resolveBroadcastErrorMessage,
  resolveBroadcastSuccessMessage,
} from './ocpiBroadcastHelpers';

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

interface CpoTariffsRequest extends ApiRequest {
  countryCode: string;
  partyId: string;
}

interface FetchCpoTariffsResponse {
  results: any[];
  limit: number;
  page: number;
}

/**
 * Fetches a page of OCPI tariffs for a CPO role from the internal gateway.
 */
export const fetchCpoTariffs = createAsyncThunk<FetchCpoTariffsResponse, CpoTariffsRequest>(
  'cpo-tariffs/fetchCpoTariffs',
  async ({ countryCode, partyId, page, limit }, { dispatch, rejectWithValue }) => {
    try {
      const resolvedPage = page || DefaultApiParameters.page;
      const resolvedLimit = limit || DefaultApiParameters.limit;
      const response = await ocpiApi.getInternalCpoTariffs(
        countryCode,
        partyId,
        resolvedPage,
        resolvedLimit,
      );
      const results = response?.data?.data ?? response?.data ?? [];
      return {
        results: Array.isArray(results) ? results : [],
        limit: resolvedLimit,
        page: resolvedPage,
      };
    } catch (error: any) {
      notify(dispatch, error?.response?.data?.message || 'Failed to fetch OCPI tariffs.', 'error');
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Broadcasts all CPO tariffs to connected partners.
 */
export const broadcastCpoTariffs = createAsyncThunk<void, { countryCode: string; partyId: string }>(
  'cpo-tariffs/broadcastCpoTariffs',
  async ({ countryCode, partyId }, { dispatch }) => {
    try {
      const response = await ocpiApi.broadcastInternalCpoTariffs(countryCode, partyId);
      const rawMessage = response?.data?.status_message || response?.status_message;
      notify(dispatch, resolveBroadcastSuccessMessage(rawMessage, 'tariffs'), 'success');
    } catch (error: any) {
      notify(dispatch, resolveBroadcastErrorMessage(error), 'error');
    }
  },
);

/**
 * Resolves the filter list for the CPO tariffs table. Filter options are known upfront (there
 * is no backend filter-list endpoint for OCPI tariffs), so this only exists to satisfy
 * CustomTable's filterListToDispatch contract.
 */
export const fetchCpoTariffFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'cpo-tariffs/fetchCpoTariffFilters',
  async () => availableFilters,
);

interface CPOTariffsInitialState {
  tariffs: any[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: 'asc' | 'desc' | null;
  sortField: string | null;
}

const initialState: CPOTariffsInitialState = {
  tariffs: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const cpoTariffsSlice = createSlice({
  name: 'cpo-tariffs',
  initialState,
  reducers: {
    /**
     * Update CPO Tariffs filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateCPOTariffsFilters: (state: any, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableCpoTariffsFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchCpoTariffs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCpoTariffs.fulfilled, (state, action) => {
        state.loading = false;
        state.tariffs = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalResults = action.payload.results.length;
        state.totalPages = Math.max(
          1,
          Math.ceil(action.payload.results.length / action.payload.limit),
        );
      })
      .addCase(fetchCpoTariffs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCpoTariffFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchCpoTariffFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchCpoTariffFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateCPOTariffsFilters, disableCpoTariffsFilterApplied, setSortData } =
  cpoTariffsSlice.actions;

export default cpoTariffsSlice.reducer;
